import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, handleApiError } from '@/infrastructure/api/client'
import type { Comment, CommentsSummaryRow, CreateCommentFormData } from '@/domains/types'

export const commentKeys = {
  byInvestigation: (investigationId: string) =>
    ['comments', 'investigation', investigationId] as const,
  summary: () => ['comments', 'summary'] as const,
}

export function useGetCommentsSummary() {
  return useQuery({
    queryKey: commentKeys.summary(),
    queryFn: async (): Promise<CommentsSummaryRow[]> => {
      const response = await apiClient.get<CommentsSummaryRow[]>('/investigations/comments/summary')
      return response.data
    },
  })
}

export function useGetCommentsByInvestigation(investigationId: string) {
  return useQuery({
    queryKey: commentKeys.byInvestigation(investigationId),
    queryFn: async (): Promise<Comment[]> => {
      const response = await apiClient.get<Comment[]>(`/investigations/${investigationId}/comments`)
      return response.data
    },
    enabled: !!investigationId,
  })
}

export function useCreateComment(investigationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateCommentFormData): Promise<Comment> => {
      const response = await apiClient.post<Comment>(
        `/investigations/${investigationId}/comments`,
        data,
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.byInvestigation(investigationId) })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useApproveComment(investigationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (commentId: string): Promise<Comment> => {
      const response = await apiClient.patch<Comment>(
        `/investigations/${investigationId}/comments/${commentId}/approve`,
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.byInvestigation(investigationId) })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useUpdateComment(investigationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      commentId,
      content,
    }: {
      commentId: string
      content: string
    }): Promise<Comment> => {
      const response = await apiClient.patch<Comment>(
        `/investigations/${investigationId}/comments/${commentId}`,
        { content },
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.byInvestigation(investigationId) })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useDeleteComment(investigationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (commentId: string): Promise<boolean> => {
      const response = await apiClient.delete<boolean>(
        `/investigations/${investigationId}/comments/${commentId}`,
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.byInvestigation(investigationId) })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}
